import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MoreHorizontal, Search, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteVarietyPricing } from "../../hooks/useVarietyPricings";
import type { VarietyPricing } from "../../services/varietyPricingApi";
import { EditVarietyPricingDialog } from "./EditVarietyPricingDialog";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";


interface VarietyPricingsTableProps {
  varietyPricings: VarietyPricing[];
  totalCount: number;
  totalPages: number;
  search: string;
  onSearchChange: (value: string) => void;
  pageNumber: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isFetching?: boolean;
}

export const VarietyPricingsTable = ({
  varietyPricings,
  totalCount,
  totalPages,
  search,
  onSearchChange,
  pageNumber,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isFetching = false,
}: VarietyPricingsTableProps) => {
  const deleteMutation = useDeleteVarietyPricing();
  const confirm = useConfirm();

  const handleDelete = (id: string, varietyName: string) => {
    confirm({
      title: "Delete variety pricing?",
      description: `Pricing for “${varietyName}” will be permanently removed. This can't be undone.`,
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        withConfirmSuccess(
          deleteMutation.mutateAsync(id),
          `Pricing for ${varietyName} deleted`
        ),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search varieties..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
        )}
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Variety</TableHead>
              <TableHead>Crop Type</TableHead>
              <TableHead>Pricing Mode</TableHead>
              <TableHead>Tray Type</TableHead>
              <TableHead>Seedling Price</TableHead>
              <TableHead>Propagation Fees</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {varietyPricings.map((pricing) => (
              <TableRow key={pricing.id}>
                <TableCell className="font-medium">{pricing.varietyName ?? "—"}</TableCell>
                <TableCell className="text-sm">
                  {pricing.cropTypeName}
                </TableCell>
                <TableCell className="text-sm">
                  <Badge variant="outline">
                    {pricing.pricingModeName || (pricing.pricingMode === 2 ? "Per Seedling/Piece" : "Per Tray")}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {pricing.pricingMode === 2
                    ? "—"
                    : pricing.trayTypeName || (pricing.trayType === 442 ? "442-Hole Tray" : "210-Hole Tray")}
                </TableCell>
                <TableCell className="text-sm">
                  {pricing.companySeedlingPrice != null
                    ? `KES ${pricing.companySeedlingPrice.toLocaleString()}`
                    : <span className="text-muted-foreground italic">Not offered</span>}
                </TableCell>
                <TableCell className="text-sm">
                  {pricing.propagationServicePrice != null
                    ? `KES ${pricing.propagationServicePrice.toLocaleString()}`
                    : <span className="text-muted-foreground italic">Not offered</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={pricing.isActive ? "default" : "destructive"}>
                    {pricing.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <EditVarietyPricingDialog pricing={pricing} />
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(pricing.id, pricing.varietyName ?? pricing.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {varietyPricings.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No variety pricings found
        </div>
      )}

      {totalCount > 0 && (
        <PaginationBar
          currentPage={pageNumber}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};